import { IPosition, IPositionType } from 'src/generated-types'
import { PositionConfig } from 'src/generated-types/configure-positions'
import { ConfigurePalette } from './configurePalette'
import { FADED_OPACITY, tint } from './configurePositions.action'
import { SHAPE_TAGS, SVG_NAMESPACE, ensureMarquee, isAnchorCircle, markPosition, tagOf } from './drawingSvgElements'

const HIT_WIDTH_PX = 16
const HALO_WIDTH_PX = 12
const DIMMED_OPACITY = '0.26'
const SELECTED_HALO_OPACITY = '0.18'
const GEOMETRY_ATTRIBUTES = ['d', 'x1', 'y1', 'x2', 'y2', 'cx', 'cy']
const SNAPSHOT_ATTRIBUTES: [string, string][] = [
  ['fill', 'data-cfg-fill'],
  ['stroke', 'data-cfg-stroke'],
  ['stroke-dasharray', 'data-cfg-dash'],
  ['opacity', 'data-cfg-opacity'],
  ['stroke-width', 'data-cfg-sw'],
]

export type PositionPaintMode = 'dim' | 'unconfigured' | 'configured' | 'selected'

const restoreOriginalStyle = (element: SVGElement) => {
  if (!element.hasAttribute('data-cfg-snap')) {
    element.setAttribute('data-cfg-snap', '1')
    SNAPSHOT_ATTRIBUTES.forEach(([name, snapshotName]) =>
      element.setAttribute(snapshotName, element.getAttribute(name) ?? ''),
    )
  }
  SNAPSHOT_ATTRIBUTES.forEach(([name, snapshotName]) => {
    const originalValue = element.getAttribute(snapshotName)
    if (originalValue) element.setAttribute(name, originalValue)
    else element.removeAttribute(name)
  })
}

const addHitLayers = (element: SVGElement, position: IPosition, selected: boolean, primary: string) => {
  const parent = element.parentNode
  if (!parent) return
  const tag = tagOf(element)
  const fill = element.getAttribute('fill')
  const isLine = tag === 'line' || (tag === 'path' && (!fill || fill === 'none'))
  const isAnchor = isAnchorCircle(element)
  if (!isLine && !isAnchor) return

  const createLayer = (className: 'cfg-hit' | 'cfg-halo') => {
    const layer = document.createElementNS(SVG_NAMESPACE, tag)
    GEOMETRY_ATTRIBUTES.forEach(name => {
      const value = element.getAttribute(name)
      if (value !== null) layer.setAttribute(name, value)
    })
    if (isAnchor) {
      const extraRadius = className === 'cfg-hit' ? 7 : 5
      layer.setAttribute('r', String(Number(element.getAttribute('r')) + extraRadius))
    }
    layer.setAttribute('class', className)
    layer.setAttribute('data-cfg-id', String(position.id))
    layer.setAttribute('data-cfg-type', String(position.type))
    layer.setAttribute('stroke-linecap', 'round')
    layer.setAttribute('stroke-linejoin', 'round')
    return layer
  }

  const haloOpacity = selected ? SELECTED_HALO_OPACITY : '0'
  const halo = createLayer('cfg-halo')
  halo.setAttribute('pointer-events', 'none')
  const hit = createLayer('cfg-hit')
  hit.style.cursor = 'pointer'
  if (isAnchor) {
    halo.setAttribute('fill', primary)
    halo.setAttribute('fill-opacity', haloOpacity)
    halo.setAttribute('stroke', 'none')
    hit.setAttribute('fill', 'transparent')
    hit.setAttribute('stroke', 'none')
    hit.setAttribute('pointer-events', 'all')
  } else {
    halo.setAttribute('fill', 'none')
    halo.setAttribute('stroke', primary)
    halo.setAttribute('stroke-width', String(HALO_WIDTH_PX))
    halo.setAttribute('stroke-opacity', haloOpacity)
    halo.setAttribute('vector-effect', 'non-scaling-stroke')
    hit.setAttribute('fill', 'none')
    hit.setAttribute('stroke', 'transparent')
    hit.setAttribute('stroke-width', String(HIT_WIDTH_PX))
    hit.setAttribute('vector-effect', 'non-scaling-stroke')
    hit.setAttribute('pointer-events', 'stroke')
  }
  parent.insertBefore(halo, element)
  parent.insertBefore(hit, element)
}

const styleLabelBox = (
  element: SVGElement,
  mode: PositionPaintMode,
  color: string | undefined,
  palette: ConfigurePalette,
) => {
  const configuredColor = mode === 'configured' ? color : undefined
  element.setAttribute('rx', '4')
  element.setAttribute(
    'fill',
    mode === 'selected' ? palette.primary : configuredColor ? tint(configuredColor, 0.16) : palette.paper,
  )
  element.setAttribute('stroke', mode === 'selected' ? palette.primary : configuredColor || palette.labelBorder)
  element.setAttribute('stroke-width', mode === 'selected' ? '2' : '1.2')
}

const styleLine = (element: SVGElement, mode: PositionPaintMode, stroke: string) => {
  element.setAttribute('stroke', stroke)
  const baseWidth = Number(element.getAttribute('stroke-width')) || 1
  const width =
    mode === 'selected' ? Math.max(baseWidth * 1.5, 3) : mode === 'configured' ? Math.max(baseWidth, 2.5) : baseWidth
  element.setAttribute('stroke-width', String(width))
  if (mode === 'unconfigured') element.setAttribute('stroke-dasharray', '8 5')
  else element.removeAttribute('stroke-dasharray')
}

export const styleNode = (
  element: SVGElement,
  mode: PositionPaintMode,
  color: string | undefined,
  palette: ConfigurePalette,
  hasLabelBox: boolean,
) => {
  restoreOriginalStyle(element)
  if (mode === 'dim') {
    element.setAttribute('opacity', DIMMED_OPACITY)
    return
  }
  element.setAttribute('opacity', '1')
  const tag = tagOf(element)
  const configuredColor = mode === 'configured' ? color : undefined
  if (tag === 'text' || tag === 'textpath') {
    if (hasLabelBox) {
      element.setAttribute('font-weight', '600')
      element.setAttribute('fill', mode === 'selected' ? palette.paper : palette.labelText)
    }
    return
  }
  if (tag === 'rect') {
    styleLabelBox(element, mode, color, palette)
    return
  }
  const stroke = mode === 'selected' ? palette.primary : configuredColor || palette.unconfigured
  const fill = element.getAttribute('fill')
  if (tag === 'path' && fill && fill !== 'none') {
    if (mode === 'selected') element.setAttribute('fill', palette.paper)
    else if (configuredColor) element.setAttribute('fill', configuredColor)
    else if (mode === 'unconfigured') element.setAttribute('fill', palette.unconfigured)
    return
  }
  if (tag === 'path' || tag === 'line') {
    styleLine(element, mode, stroke)
    return
  }
  if (tag === 'circle' && Number(element.getAttribute('r') || 0) > 0 && isAnchorCircle(element)) {
    element.setAttribute(
      'fill',
      mode === 'selected' ? palette.primary : configuredColor ? tint(configuredColor, 0.22) : palette.paper,
    )
    element.setAttribute('stroke', stroke)
  }
}

const hasLabelBox = (nodes: Element[]) => nodes.some(node => tagOf(node) === 'rect')

export const getPaintMode = (
  position: IPosition,
  activeType: IPositionType,
  selectedIds: Set<number>,
  configById: Map<number, PositionConfig>,
): PositionPaintMode => {
  if (position.type !== activeType) return 'dim'
  if (selectedIds.has(position.id)) return 'selected'
  return configById.has(position.id) ? 'configured' : 'unconfigured'
}

export interface PaintDrawingOptions {
  positions: IPosition[]
  cages: IPosition[]
  activeType: IPositionType
  selectedIds: Set<number>
  configById: Map<number, PositionConfig>
  palette: ConfigurePalette
}

export const paintDrawing = (svg: SVGSVGElement, options: PaintDrawingOptions) => {
  const { positions, cages, activeType, selectedIds, configById, palette } = options
  const positionShapes = new Map<number, SVGGraphicsElement>()
  const cageShapes = new Map<number, SVGGraphicsElement>()
  const firstShape = (nodes: Element[]) =>
    nodes.find(node => SHAPE_TAGS.has(tagOf(node))) as SVGGraphicsElement | undefined

  positions.forEach(position => {
    const nodes = markPosition(svg, position)
    const shape = firstShape(nodes)
    const isActiveType = position.type === activeType
    if (shape && isActiveType) positionShapes.set(position.id, shape)
    const mode = getPaintMode(position, activeType, selectedIds, configById)
    const labelBox = hasLabelBox(nodes)
    nodes.forEach(node => {
      const element = node as SVGElement
      element.style.cursor = 'pointer'
      styleNode(element, mode, configById.get(position.id)?.color, palette, labelBox)
    })
    if (isActiveType) {
      nodes.forEach(node => addHitLayers(node as SVGElement, position, mode === 'selected', palette.primary))
    }
  })

  cages.forEach(cage => {
    const nodes = markPosition(svg, cage)
    const shape = firstShape(nodes)
    if (shape) cageShapes.set(cage.id, shape)
    nodes.forEach(node => {
      const element = node as SVGElement
      element.setAttribute('opacity', '1')
      element.style.cursor = activeType === IPositionType.MooringLine ? 'default' : 'pointer'
    })
  })

  svg.style.cursor = 'crosshair'
  return { positionShapes, cageShapes, marquee: ensureMarquee(svg, palette) }
}

export const paintHighlight = (host: HTMLElement, options: PaintDrawingOptions & { highlightedIds: Set<number> }) => {
  const { positions, activeType, selectedIds, configById, palette, highlightedIds } = options
  const typeById = new Map(positions.map(position => [position.id, position.type]))
  configById.forEach((config, positionId) => {
    if (selectedIds.has(positionId) || typeById.get(positionId) !== activeType) return
    const isHighlighted = highlightedIds.has(positionId)
    const isFaded = highlightedIds.size > 0 && !isHighlighted
    const nodes = [...host.querySelectorAll<SVGElement>(`[data-cfg-id="${positionId}"]:not(.cfg-hit):not(.cfg-halo)`)]
    const labelBox = hasLabelBox(nodes)
    nodes.forEach(element => {
      styleNode(element, 'configured', isHighlighted ? palette.primary : config.color, palette, labelBox)
      const tag = tagOf(element)
      if (isHighlighted && (tag === 'path' || tag === 'line')) {
        const width = Number(element.getAttribute('stroke-width')) || 1
        element.setAttribute('stroke-width', String(Math.max(width * 1.6, 4)))
      }
      if (isFaded) element.setAttribute('opacity', FADED_OPACITY)
    })
  })
}
